import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, Award, UserCheck, Users, IndianRupee, Shield, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../lib/api";
import { inr, fdate, monthLabel, currentMonth } from "../lib/format";
import { PageHeader, FormDialog, ConfirmDialog, DataTable, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";

export default function Agents() {
  const { user, isAdmin, isDirector, isStaff, isLead } = useAuth();
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [directorsList, setDirectorsList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [activeTab, setActiveTab] = useState("all"); // all | director | staff | agent
  const [form, setForm] = useState({ open: false, initial: null });
  const [del, setDel] = useState(null);
  const [view, setView] = useState(null);
  const [dash, setDash] = useState(null);
  const [appointModal, setAppointModal] = useState({ open: false, agent: null, role: "staff", lead_code: "", team_id: "" });
  const [payCommModal, setPayCommModal] = useState({ open: false, sale: null, amount: "", notes: "", who: "agent" });

  const load = useCallback(async () => {
    try {
      const [agRes, tmRes, dirRes, stfRes] = await Promise.all([
        api.get("/agents"),
        api.get("/teams"),
        api.get("/directors-list"),
        api.get("/staff-list"),
      ]);
      setAgents(agRes.data);
      setTeams(tmRes.data);
      setDirectorsList(dirRes.data || []);
      setStaffList(stfRes.data || []);
    } catch (err) {
      toast.error(errMsg(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (view) {
      api.get(`/agents/${view.agent_code}/dashboard?month=${currentMonth()}`).then((r) => setDash(r.data));
    }
  }, [view]);

  const directors = agents.filter((a) => a.role === "director");
  const staffMembers = agents.filter((a) => a.role === "staff");
  const salesAgents = agents.filter((a) => a.role !== "director" && a.role !== "staff");

  const filteredAgents = agents.filter((a) => {
    if (isStaff) {
      return a.staff_code === user.agent_code || a.role === "agent" || a.role === "associate";
    }
    if (isDirector) {
      if (activeTab === "staff") return a.role === "staff";
      if (activeTab === "agent") return a.role !== "staff" && a.role !== "director";
      return true;
    }
    if (isAdmin) {
      if (activeTab === "director") return a.role === "director";
      if (activeTab === "staff") return a.role === "staff";
      if (activeTab === "agent") return a.role !== "director" && a.role !== "staff";
      return true;
    }
    return true;
  });

  const STAFF_DESIGNATION_OPTIONS = [
    { value: "Deputy Director", label: "Deputy Director" },
    { value: "General Manager", label: "General Manager" },
    { value: "Deputy General Manager", label: "Deputy General Manager" },
    { value: "Assistant General Manager", label: "Assistant General Manager" },
    { value: "Marketing Manager", label: "Marketing Manager" },
    { value: "Relationship Officer", label: "Relationship Officer" },
    { value: "Relationship Advisor", label: "Relationship Advisor" },
  ];

  let formFields = [];
  if (isStaff) {
    formFields = [
      { name: "agent_code", label: "Agent Code / ID", required: true, placeholder: "e.g. AGT015 or NIA..." },
      { name: "name", label: "Agent Full Name", required: true },
      { name: "mobile", label: "Mobile Number", required: true },
      {
        name: "designation",
        label: "Designation",
        type: "select",
        options: [
          { value: "Sales Executive", label: "Sales Executive" },
          { value: "Senior Sales Executive", label: "Senior Sales Executive" },
          { value: "Associate", label: "Associate" },
          { value: "Business Associate", label: "Business Associate" },
        ],
      },
      { name: "joining_date", label: "Joining Date", type: "date" },
      { name: "commission_rate", label: "Commission % Rate", type: "number", placeholder: "2.0" },
      {
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
      {
        name: "password",
        label: "Initial Login Password",
        placeholder: "Default: nest@123",
        hint: `Agent will be recruited and managed directly under you (${user.name})`,
      },
    ];
  } else if (isDirector) {
    formFields = [
      { name: "agent_code", label: "Staff ID", required: true, placeholder: "e.g. STF001 or NIA..." },
      { name: "name", label: "Staff Full Name", required: true },
      { name: "mobile", label: "Mobile Number", required: true },
      {
        name: "designation",
        label: "Staff Designation",
        type: "select",
        required: true,
        options: STAFF_DESIGNATION_OPTIONS,
      },
      { name: "joining_date", label: "Joining Date", type: "date" },
      {
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
      {
        name: "password",
        label: "Initial Login Password",
        placeholder: "Default: nest@123",
        hint: `Staff member will report directly to you (${user.name})`,
      },
    ];
  } else {
    // Admin
    formFields = [
      { name: "agent_code", label: "User / Staff / Agent ID", required: true, placeholder: "e.g. DIR002, STF010, NIA015" },
      { name: "name", label: "Full Name", required: true },
      { name: "mobile", label: "Mobile Number", required: true },
      {
        name: "role",
        label: "Role Tier",
        type: "select",
        required: true,
        options: [
          { value: "director", label: "Director" },
          { value: "staff", label: "Staff Member" },
          { value: "agent", label: "Sales Agent / Associate" },
        ],
      },
      {
        name: "director_code",
        label: "Assign Under Director (for Staff/Agents)",
        type: "select",
        options: [
          { value: "", label: "None / Top Executive" },
          ...directorsList.map((d) => ({ value: d.agent_code, label: `${d.name} (${d.agent_code})` })),
        ],
      },
      {
        name: "staff_code",
        label: "Recruited by Staff Member (for Agents)",
        type: "select",
        options: [
          { value: "", label: "None / Direct" },
          ...staffList.map((s) => ({ value: s.agent_code, label: `${s.name} (${s.designation || "Staff"} · ${s.agent_code})` })),
        ],
      },
      {
        name: "designation",
        label: "Designation",
        type: "select",
        options: [
          { value: "Director", label: "Director" },
          ...STAFF_DESIGNATION_OPTIONS,
          { value: "Sales Executive", label: "Sales Executive" },
          { value: "Senior Sales Executive", label: "Senior Sales Executive" },
          { value: "Associate", label: "Associate" },
          { value: "Business Associate", label: "Business Associate" },
        ],
      },
      { name: "joining_date", label: "Joining Date", type: "date" },
      { name: "commission_rate", label: "Standard Commission %", type: "number", placeholder: "2.0" },
      {
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ],
      },
      {
        name: "password",
        label: "Login Password",
        placeholder: "Default: nest@123",
        hint: "User logs in with their ID/Email + this password",
      },
    ];
  }

  const submitForm = async (v) => {
    try {
      let role = "agent";
      let director_code = "";
      let staff_code = "";
      let designation = v.designation || "";

      if (isStaff) {
        role = "agent";
        staff_code = user.agent_code;
        director_code = user.director_code || "";
        designation = designation || "Associate";
      } else if (isDirector) {
        role = "staff";
        director_code = user.agent_code;
        staff_code = "";
        designation = designation || "General Manager";
      } else if (isAdmin) {
        role = v.role || "agent";
        director_code = v.director_code || "";
        staff_code = v.staff_code || "";
        if (role === "director") {
          designation = "Director";
        }
      }

      const payload = {
        agent_code: v.agent_code,
        name: v.name,
        mobile: v.mobile,
        role,
        director_code,
        staff_code,
        lead_code: staff_code || director_code || user.agent_code,
        team_id: v.team_id || "",
        designation,
        joining_date: v.joining_date || "",
        commission_plan: v.commission_plan || "",
        commission_rate: parseFloat(v.commission_rate) || 2.0,
        status: v.status || "active",
        password: v.password || "",
      };

      if (form.initial?.id) {
        await api.put(`/agents/${form.initial.id}`, payload);
        toast.success("Profile updated successfully");
      } else {
        await api.post("/agents", payload);
        toast.success(`Registered successfully (${payload.agent_code.toUpperCase()})`);
      }
      setForm({ open: false, initial: null });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const handleAppointRole = async (e) => {
    e.preventDefault();
    if (!appointModal.agent) return;
    try {
      await api.post(`/agents/${appointModal.agent.id}/appoint-role`, {
        role: appointModal.role,
        lead_code: appointModal.lead_code || "",
        team_id: appointModal.team_id || "",
      });
      toast.success(
        appointModal.role === "team_lead"
          ? `${appointModal.agent.name} is now appointed as Team Leader!`
          : `${appointModal.agent.name} is now designated as Sales Agent.`
      );
      setAppointModal({ open: false, agent: null, role: "team_lead", lead_code: "", team_id: "" });
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const handlePayCommission = async (e) => {
    e.preventDefault();
    if (!payCommModal.sale) return;
    try {
      await api.post("/commission/pay", {
        sale_id: payCommModal.sale.id,
        who: payCommModal.who,
        amount: parseFloat(payCommModal.amount) || 0,
        notes: payCommModal.notes,
      });
      toast.success("Commission payment recorded successfully!");
      setPayCommModal({ open: false, sale: null, amount: "", notes: "", who: "agent" });
      if (view) {
        api.get(`/agents/${view.agent_code}/dashboard?month=${currentMonth()}`).then((r) => setDash(r.data));
      }
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  // Calculations
  const totalSales = agents.reduce((s, a) => s + (a.total_sales || 0), 0);
  const totalBookings = agents.reduce((s, a) => s + (a.total_bookings || 0), 0);
  const totalCommPending = agents.reduce((s, a) => s + (a.commission_pending || 0), 0);

  return (
    <div data-testid="agents-page" className="space-y-6">
      <PageHeader
        title={
          isAdmin
            ? "Staff & Hierarchy"
            : isDirector
            ? "Staff & Hierarchy"
            : "My Recruited Agents"
        }
        sub={
          isAdmin
            ? "Manage Directors, Staff hierarchy, and Sales Agents across all projects"
            : isDirector
            ? `Staff managed under your hierarchy (${user.name} · Director)`
            : `Agents recruited and managed directly by you (${user.name} · ${user.designation || "Staff"})`
        }
        testid="agents-header"
      >
        <Button
          className="bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm"
          onClick={() => setForm({ open: true, initial: null })}
          data-testid="add-agent-button"
        >
          <Plus className="mr-1.5 h-4 w-4" />{" "}
          {isAdmin ? "Add Personnel" : isDirector ? "Add Staff Member" : "Recruit Agent"}
        </Button>
      </PageHeader>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {isAdmin ? (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Directors</span>
                <Shield className="h-4 w-4 text-indigo-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{directors.length}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Top Executives</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Staff Members</span>
                <UserCheck className="h-4 w-4 text-amber-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{staffMembers.length}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">GM, DGM, Managers & ROs</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sales Agents</span>
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{salesAgents.length}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Recruited by Staff</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Total Sales</span>
                <ArrowUpRight className="h-4 w-4 text-blue-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{inr(totalSales)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{totalBookings} Total Plot Bookings</p>
            </div>
          </>
        ) : isDirector ? (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">My Staff</span>
                <UserCheck className="h-4 w-4 text-amber-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{staffMembers.length}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Reporting to you</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recruited Agents</span>
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{salesAgents.length}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Under your staff</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Hierarchy Sales</span>
                <ArrowUpRight className="h-4 w-4 text-blue-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{inr(totalSales)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{totalBookings} Bookings</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pending Comm.</span>
                <IndianRupee className="h-4 w-4 text-rose-500" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-rose-700">{inr(totalCommPending)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">To be distributed</p>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">My Agents</span>
                <Users className="h-4 w-4 text-emerald-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{salesAgents.length}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Recruited by you</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Team Sales</span>
                <ArrowUpRight className="h-4 w-4 text-blue-600" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{inr(totalSales)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">{totalBookings} Bookings</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Performance</span>
                <Award className="h-4 w-4 text-amber-500" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-slate-900">{totalBookings}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Closed Deals</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pending Comm.</span>
                <IndianRupee className="h-4 w-4 text-rose-500" />
              </div>
              <p className="mt-2 font-num text-2xl font-bold text-rose-700">{inr(totalCommPending)}</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Pipeline</p>
            </div>
          </>
        )}
      </div>

      {/* Tabs Filter for Admin / Director */}
      {isAdmin && (
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          {[
            { id: "all", label: `All Personnel (${agents.length})` },
            { id: "director", label: `🏛️ Directors (${directors.length})` },
            { id: "staff", label: `👤 Staff Members (${staffMembers.length})` },
            { id: "agent", label: `💼 Sales Agents (${salesAgents.length})` },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
                activeTab === tab.id
                  ? "bg-slate-900 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {isDirector && (
        <div className="flex gap-2 border-b border-slate-200 pb-2">
          {[
            { id: "all", label: `All My Hierarchy (${agents.length})` },
            { id: "staff", label: `👤 My Staff (${staffMembers.length})` },
            { id: "agent", label: `💼 Agents under Staff (${salesAgents.length})` },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
                activeTab === tab.id
                  ? "bg-slate-900 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Agents & Leads Table */}
      <DataTable
        testid="agents-table"
        columns={[
          {
            key: "agent_code",
            label: "ID",
            render: (r) => (
              <span className="font-num font-semibold text-slate-900">{r.agent_code}</span>
            ),
          },
          {
            key: "name",
            label: "Name & Role",
            render: (r) => (
              <div>
                <p className="font-medium text-slate-900">{r.name}</p>
                <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                  {r.role === "director" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700 border border-indigo-200">
                      🏛️ Director
                    </span>
                  ) : r.role === "staff" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 border border-amber-200">
                      👤 Staff · {r.designation || "Staff"}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700 border border-blue-200">
                      💼 Agent · {r.designation || "Associate"}
                    </span>
                  )}
                </div>
              </div>
            ),
          },
          { key: "mobile", label: "Mobile" },
          {
            key: "hierarchy",
            label: "Hierarchy / Managed By",
            render: (r) => {
              if (r.role === "director") {
                return <span className="text-xs font-semibold text-indigo-600">Top Director (Managed by Admin)</span>;
              }
              if (r.role === "staff") {
                return (
                  <div>
                    <span className="text-xs font-medium text-slate-800">Director: {r.director_name || r.director_code || "Admin"}</span>
                  </div>
                );
              }
              return (
                <div>
                  <span className="text-xs font-medium text-slate-800">Recruited By: {r.staff_name || r.staff_code || r.lead_name || "Staff"}</span>
                  {r.director_name && (
                    <p className="text-[10px] text-slate-400">Under: {r.director_name}</p>
                  )}
                </div>
              );
            },
          },
          {
            key: "total_sales",
            label: "Sales / Bookings",
            render: (r) => (
              <div>
                <p className="font-num text-xs font-bold text-slate-900">{inr(r.total_sales || 0)}</p>
                <p className="text-[10px] text-slate-400">{r.total_bookings || 0} plots</p>
              </div>
            ),
          },
          {
            key: "commission_pending",
            label: "Pending Comm.",
            align: "right",
            render: (r) => (
              <span
                className={`font-num text-xs font-bold ${
                  (r.commission_pending || 0) > 0 ? "text-amber-700" : "text-emerald-700"
                }`}
              >
                {inr(r.commission_pending || 0)}
              </span>
            ),
          },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          {
            key: "actions",
            label: "Actions",
            render: (r) => (
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label="View performance"
                  title="View Performance & Commissions"
                  onClick={() => {
                    setView(r);
                    setDash(null);
                  }}
                >
                  <Eye className="h-4 w-4 text-slate-600" />
                </Button>

                {isAdmin && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Appoint role"
                    title="Appoint as Team Lead / Change Reporting"
                    className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                    onClick={() =>
                      setAppointModal({
                        open: true,
                        agent: r,
                        role: r.role === "team_lead" ? "agent" : "team_lead",
                        lead_code: r.lead_code || "",
                        team_id: r.team_id || "",
                      })
                    }
                  >
                    <Award className="h-4 w-4" />
                  </Button>
                )}

                {(isAdmin || isLead) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Edit agent"
                    onClick={() => setForm({ open: true, initial: { ...r, password: "" } })}
                  >
                    <Pencil className="h-4 w-4 text-slate-600" />
                  </Button>
                )}

                {(isAdmin || (isLead && r.lead_code === user.agent_code && r.role !== "team_lead")) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Delete agent"
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                    onClick={() => setDel(r)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ),
          },
        ]}
        rows={filteredAgents}
        onRowClick={(r) => {
          setView(r);
          setDash(null);
        }}
      />

      {/* Appoint as Team Lead / Change Role Modal */}
      <Dialog
        open={appointModal.open}
        onOpenChange={(o) => setAppointModal({ ...appointModal, open: o })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2">
              <Award className="h-5 w-5 text-purple-600" />
              Appoint Role for {appointModal.agent?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAppointRole} className="space-y-4 pt-2">
            <div>
              <Label className="text-xs font-semibold text-slate-700">Designated Role</Label>
              <select
                value={appointModal.role}
                onChange={(e) => setAppointModal({ ...appointModal, role: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none"
              >
                <option value="team_lead">👑 Team Leader (Manages Agents & Commissions)</option>
                <option value="agent">💼 Sales Agent (Reports to Team Lead)</option>
              </select>
              <p className="mt-1 text-[11px] text-slate-500">
                {appointModal.role === "team_lead"
                  ? "Team Leaders can log in to view their agents, assign leads, and give commissions."
                  : "Sales Agents report to an appointed Team Leader and view their sales."}
              </p>
            </div>

            {appointModal.role === "agent" && (
              <div>
                <Label className="text-xs font-semibold text-slate-700">Assign Reporting Team Leader</Label>
                <select
                  value={appointModal.lead_code}
                  onChange={(e) => setAppointModal({ ...appointModal, lead_code: e.target.value })}
                  className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none"
                >
                  <option value="">Direct to Admin (No Team Leader)</option>
                  {teamLeads
                    .filter((l) => l.agent_code !== appointModal.agent?.agent_code)
                    .map((l) => (
                      <option key={l.agent_code} value={l.agent_code}>
                        {l.name} ({l.agent_code})
                      </option>
                    ))}
                </select>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold text-slate-700">Assigned Team</Label>
              <select
                value={appointModal.team_id}
                onChange={(e) => setAppointModal({ ...appointModal, team_id: e.target.value })}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-emerald-600 focus:outline-none"
              >
                <option value="">No Team</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setAppointModal({ open: false, agent: null, role: "team_lead", lead_code: "", team_id: "" })}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-purple-700 hover:bg-purple-800 text-white">
                Save & Appoint Role
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Agent/Lead Performance & Commission Modal */}
      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="agent-dashboard-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between pr-6">
              <div>
                <DialogTitle className="font-display text-xl flex items-center gap-2">
                  {view?.name}
                  <span className="font-num text-sm text-slate-400">({view?.agent_code})</span>
                  {view?.role === "team_lead" || view?.is_team_lead ? (
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
                      👑 Team Leader
                    </span>
                  ) : view?.role === "staff" ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                      👤 Staff
                    </span>
                  ) : (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">
                      💼 Associate
                    </span>
                  )}
                </DialogTitle>
                {dash?.lead_info && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Reports to: <span className="font-semibold text-slate-700">{dash.lead_info.name}</span> ({dash.lead_info.agent_code})
                  </p>
                )}
              </div>
            </div>
          </DialogHeader>

          {!dash ? (
            <div className="grid grid-cols-2 gap-3 animate-pulse">
              {[...Array(6)].map((_, i) => (
                <div key={`skel-${i}`} className="h-16 rounded-md bg-slate-100" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[
                  ["Total Sales", inr(dash.total_sales)],
                  ["Monthly Sales", inr(dash.monthly_sales)],
                  ["Total Bookings", `${dash.total_bookings} plots`],
                  ["Total Collection", inr(dash.total_collection)],
                  ["Commission Earned", inr(dash.commission_earned)],
                  ["Commission Paid", inr(dash.commission_paid)],
                  ["Commission Pending", inr(dash.commission_pending)],
                  ["Monthly Collection", inr(dash.monthly_collection)],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-slate-200 p-3 bg-slate-50/60">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-1 font-num text-sm font-bold text-slate-900">{v}</p>
                  </div>
                ))}
              </div>

              {/* Commission Distribution & Payout Action */}
              {(isAdmin || isLead) && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-emerald-900 flex items-center gap-1.5">
                        <IndianRupee className="h-4 w-4 text-emerald-700" />
                        Commission Management & Distribution
                      </p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        Current pending commission: <span className="font-bold">{inr(dash.commission_pending)}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <h3 className="font-display text-sm font-semibold text-slate-800 pt-2">Recent Sales & Commission Status</h3>
              <DataTable
                testid="agent-sales-table"
                columns={[
                  { key: "date", label: "Date", render: (r) => fdate(r.date) },
                  { key: "project_name", label: "Project" },
                  { key: "plot_number", label: "Plot" },
                  { key: "sale_amount", label: "Sale Amount", align: "right", render: (r) => inr(r.sale_amount) },
                  {
                    key: "agent_commission",
                    label: "Commission",
                    align: "right",
                    render: (r) => (
                      <div>
                        <p className="font-num text-xs font-bold text-slate-900">{inr(r.agent_commission)}</p>
                        <p className="text-[10px] text-slate-500">Paid: {inr(r.agent_commission_paid || 0)}</p>
                      </div>
                    ),
                  },
                  {
                    key: "commission_pending",
                    label: "Pending",
                    align: "right",
                    render: (r) => {
                      const pend = (r.agent_commission || 0) - (r.agent_commission_paid || 0);
                      return (
                        <span className={`font-num text-xs font-bold ${pend > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                          {inr(pend)}
                        </span>
                      );
                    },
                  },
                  {
                    key: "pay_action",
                    label: "",
                    render: (r) => {
                      const pend = (r.agent_commission || 0) - (r.agent_commission_paid || 0);
                      if (!isAdmin && !isLead) return null;
                      return (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={pend <= 0}
                          className="h-7 text-xs bg-white text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                          onClick={() =>
                            setPayCommModal({
                              open: true,
                              sale: r,
                              amount: pend.toString(),
                              notes: `Commission payout for Plot ${r.plot_number} (${r.project_name})`,
                              who: "agent",
                            })
                          }
                        >
                          Give Comm.
                        </Button>
                      );
                    },
                  },
                ]}
                rows={dash.recent_sales || []}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pay / Distribute Commission Modal */}
      <Dialog
        open={payCommModal.open}
        onOpenChange={(o) => setPayCommModal({ ...payCommModal, open: o })}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-lg flex items-center gap-2 text-emerald-800">
              <IndianRupee className="h-5 w-5 text-emerald-600" />
              Pay Commission to {payCommModal.sale?.agent_name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePayCommission} className="space-y-4 pt-2">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
              <p>
                <span className="text-slate-500">Project / Plot:</span>{" "}
                <span className="font-semibold text-slate-800">
                  {payCommModal.sale?.project_name} · Plot {payCommModal.sale?.plot_number}
                </span>
              </p>
              <p>
                <span className="text-slate-500">Customer:</span>{" "}
                <span className="font-semibold text-slate-800">{payCommModal.sale?.customer_name}</span>
              </p>
              <p>
                <span className="text-slate-500">Sale Value:</span>{" "}
                <span className="font-semibold text-slate-800">{inr(payCommModal.sale?.sale_amount || 0)}</span>
              </p>
              <p>
                <span className="text-slate-500">Total Commission:</span>{" "}
                <span className="font-semibold text-slate-800">{inr(payCommModal.sale?.agent_commission || 0)}</span>
              </p>
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Payment Amount (₹)</Label>
              <Input
                type="number"
                required
                min="1"
                step="any"
                value={payCommModal.amount}
                onChange={(e) => setPayCommModal({ ...payCommModal, amount: e.target.value })}
                className="mt-1"
                placeholder="Enter amount in ₹"
              />
            </div>

            <div>
              <Label className="text-xs font-semibold text-slate-700">Payout Remarks / Notes</Label>
              <Input
                type="text"
                value={payCommModal.notes}
                onChange={(e) => setPayCommModal({ ...payCommModal, notes: e.target.value })}
                className="mt-1"
                placeholder="UPI Ref / Cheque / Bank Transfer"
              />
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPayCommModal({ open: false, sale: null, amount: "", notes: "", who: "agent" })}
              >
                Cancel
              </Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                Record Payout
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Form Dialog */}
      <FormDialog
        open={form.open}
        onOpenChange={(o) => setForm({ open: o, initial: null })}
        title={
          form.initial
            ? isStaff
              ? "Edit Agent Profile"
              : isDirector
              ? "Edit Staff Profile"
              : "Edit Personnel Profile"
            : isStaff
            ? "Recruit New Agent"
            : isDirector
            ? "Add Staff Member"
            : "Add Personnel (Director / Staff / Agent)"
        }
        fields={formFields}
        initial={form.initial ? {
          ...form.initial,
          role: form.initial.role === "staff" ? "staff" : "associate",
        } : null}
        onSubmit={submitForm}
        testid="agent-form-dialog"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!del}
        onOpenChange={(o) => !o && setDel(null)}
        title="Delete Staff Record"
        desc={`Are you sure you want to delete ${del?.name} (${del?.agent_code})? This will also remove their login credentials.`}
        onConfirm={async () => {
          try {
            await api.delete(`/agents/${del.id}`);
            toast.success("Staff profile deleted");
            setDel(null);
            load();
          } catch (err) {
            toast.error(errMsg(err));
          }
        }}
      />
    </div>
  );
}
